/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { TicketContainer } from '@appPlanApplication/tickets/TicketContainer';
import { Point, Rectangle } from 'pixi.js-legacy';
import { clamp } from 'utils/basic-utils';
import { Side } from '@touchplan/common';
import { TicketUtils } from '@appCanvasCommon/utils/TicketUtils';
import { Ticket } from '@touchplan/common';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { LEVEL_OF_DETAIL_TABLE, LevelOfDetail } from '@appPlanApplication/lod/LevelOfDetail';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { TicketType } from '@touchplan/common';
import { TicketTextSettings } from './TicketTextFontPath';
import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { FancyTextStyle } from '@appCanvasCommon/interfaces/fancy-text-style.interface';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { ITrimTextData, trimText } from '@appCanvasCommon/utils/trim-text';
import { removeUnitFromString } from '@appCanvasCommon/utils/remove-unit-from-string';

export class TicketTextCanvas extends HighResolutionText implements ComponentInterface {
	public override active = true;
	public override visible = true;
	public align = { x: false, y: false };
	public maskArea: Rectangle = new Rectangle(0, 0, 1, 1);
	protected ticket: TicketContainer;

	constructor(
		text: string,
		style: FancyTextStyle,
		canvasAngularLand: CanvasAngularLand,
		ticketContainer: TicketContainer,
		canvas?: HTMLCanvasElement
	) {
		super(text, style, canvas);

		this.ticket = ticketContainer;
		this.tint = this.ticket.liveRoleTextColor;
		this.eventMode = EventMode.None;
	}

	public positionCenter(ticket: Ticket) {
		const textCenter: Point = new Point(this.width / 2, this.height / 2);
		const drawCenter: Point = new Point(
			this.maskArea.x + this.maskArea.width / 2,
			this.maskArea.y + this.maskArea.height / 2
		);
		if (this.align.x) {
			this.x = drawCenter.x - textCenter.x;
		}
		if (this.align.y) {
			this.y = clamp(drawCenter.y - textCenter.y, this.maskArea.y);
		}
	}

	protected _getHeightOffset(ticket: Ticket): number {
		let _offset = 0;
		const type = TicketUtils.getTicketType(ticket);
		if (ticket.view.location && type === TicketType.Task) {
			_offset += ticket.view.side === Side.PULL ? 40 : 25;
		}
		if (
			ticket.view.liveStart ||
			(ticket.view.liveFinish && LevelOfDetail.getLODLevel() >= LEVEL_OF_DETAIL_TABLE.MEDIUM)
		) {
			_offset += ticket.view.side === Side.PULL ? 36 : 20;
		}

		return _offset;
	}

	/**
	 * Applies offset and drawing area settings depending on the ticket type.
	 * @param {TicketContainer} ticketContainer
	 * @param {TicketTextSettings} settings
	 */
	protected applyOffsetAndDrawArea(ticketContainer: TicketContainer, settings: TicketTextSettings) {
		const data = ticketContainer.getData();
		const layout = ticketContainer.getLayout();
		const type = TicketUtils.getTicketType(data).toUpperCase();
		// Append a '_PULL' suffix if required.
		const pullSuffix = data.view.side === Side.PULL ? '_PULL' : '';

		// Set a width and height. This is so we can override the width or height from the ticket data.
		const width = settings[type + '_DRAWING_AREA_WIDTH' + pullSuffix] || layout.width;
		const height = settings[type + '_DRAWING_AREA_HEIGHT' + pullSuffix] || layout.height;

		// Set text offset (Changes depending on the active components
		this.style.wordWrapWidth =
			width - settings[type + '_RIGHT_MARGIN' + pullSuffix] - settings[type + '_LEFT_MARGIN' + pullSuffix];
		this.maskArea.x = settings[type + '_LEFT_MARGIN' + pullSuffix];
		this.maskArea.y = settings[type + '_TOP_MARGIN' + pullSuffix];

		// Set text drawing area.
		this.maskArea.width = this.style.wordWrapWidth as number;
		this.maskArea.height = height - settings[type + '_BOTTOM_MARGIN' + pullSuffix];
	}

	/**
	 * When text changes, re-calculate the trig data.
	 * @param {string} newText
	 * @param {string} oldText
	 */
	public onTextChanged = (newText: string, oldText: string) => {
		this.setText(newText);
	};
	/**
	 * Override this to change where the text data comes from
	 * @param text
	 */
	public setText(text: string) {
		if (!text && text !== this.text) {
			return;
		}

		this.text = this.processText(text);
	}

	public override destroy(options?): void {
		super.destroy(options);
	}

	/**
	 * This method modifies input text and returns a potentially modified value for final render.
	 * Currently this runs the text through the `trimText` function which slices it to fit inside a rectangle
	 * @param text
	 * @protected
	 */
	protected processText(text: string): string {
		if (!this.enableTrimText) {
			// We are applying a concat with an empty string as a safety in case a user enters only a number as a text input.
			return ``.concat(text);
		}

		return trimText(``.concat(text), this.createTrimTextConfig());
	}

	private createTrimTextConfig(): ITrimTextData {
		return {
			fontSize: removeUnitFromString(this.style.fontSize),
			fontFamily: this.style.fontFamily as string,
			maxWidth: this.maskArea ? this.maskArea.width : Number(this.style.wordWrapWidth),
			maxHeight: this.maskArea ? this.maskArea.height : this.height,
			lineHeight: removeUnitFromString(this.style.lineHeight),
			extraLines: 1,
		};
	}
}
